// Shipping estimate shown on the bracket page. MUST stay in sync with the
// Shopify delivery profile (Settings → Shipping and delivery) and the bracket
// variant weight in Shopify (550g — real packed weight incl. packaging share,
// calibrated from AusPost Band 3 receipts on 2026-07-05).
//
// Live tiers, the same in every state up to 150 brackets (82.5kg):
//   0–1kg $10.95 · 1–3kg $15.95 · 3–6kg $23.95 · 6–10kg $29.95
//   10–13kg $32.95 · 13–20kg $49.95 · 20–23kg $77.95 · 23–30kg $84.95
//   30–40kg $100 · 40–60kg $100 · 60–82.5kg $125
// Past 150 brackets it goes by state (see BULK_TIERS_FROM below).
//
// Since 2026-10-08 brackets, flat brackets and kits sit on their own "Brackets
// & Kits" delivery profile, zoned VIC / NSW+ACT+SA+TAS / QLD / WA / NT. Up to
// 82.5kg every zone carries the national ladder above, unchanged. Over it each
// zone is priced at AusPost cost (same method as the extensions below), capped
// at a $550 pallet per 500 kg, up to 5,000 kg. The old General profile is left
// in place as a fallback and for any new product Shopify drops onto it.
//
// History of the 20kg+ bands: raised to $95 / $145 / $160 on 2026-09-05, pulled
// back to $62.95 / $69.95 / $125 on 2026-09-08 as too steep, then the first two
// lifted $15 to their current numbers the same day. Tanner's calls, trading
// margin for conversion on heavy orders.
//
// The heavy end was rebuilt on 2026-09-10 after a 55kg / $675 order (100
// brackets to Narromine NSW) hit the old 40kg ceiling: 30–40kg cut $125 → $100,
// 40–60kg opened at $100, 60–90kg opened at $125. With no rate to return
// Shopify rejects the checkout as "we don't ship to your address", so the
// customer reads it as an address fault and never learns it was a weight limit.
// That order was lost to it and came in through the enquiry form instead.
//
// Keep every band non-decreasing. A 35kg order must never post cheaper than a
// 55kg one, or splitting an order becomes the cheaper way to buy. The bands
// still deactivated above 90kg have that fault baked in (130–180kg sits at
// $260, under the $295 on 40–60kg) so do not just switch them on as they are.
//
// What they cover. AusPost's domestic limit is 22kg, so anything heavier ships
// as two or more parcels and the real cost swings hard by destination. Against
// the metro cost at each band's TOP weight:
//   20–23kg  $77.95 charged vs ~$74 to Melbourne     → just covers metro
//   23–30kg  $84.95 charged vs ~$93 to Melbourne     → ~$8 short to metro
//   30–40kg  $100 charged vs ~$115 to Melbourne      → ~$15 short to metro
//   40–60kg  $100 charged vs ~$230 est. (3 parcels)  → heavy loss
//   60–90kg  $125 charged vs ~$350 est. (5 parcels)  → heavier loss
// Interstate is a different story: 30kg to Perth is roughly $262, so a heavy
// order heading west loses money at any of these numbers. The top three bands
// are a deliberate loss leader to win big orders, Tanner's call on 2026-09-10,
// on the basis that the order value carries the freight. Only the sub-30kg
// figures come from real receipts; the 40kg+ comparisons are estimates.
//
// ABOVE 5,000kg there is NO automatic rate. bracketShippingEstimate() returns
// { type: "pickup-only" } there and callers show the "contact us for freight"
// line. The checkout past it fails with the misleading address error described
// above, so the CART catches it first: see needsFreightQuote() below and the
// pallet-quote panel in CartDrawer.
// Free local pickup stays enabled at checkout (Resale Therapy on Main,
// Emerald VIC) at any weight. Macclesfield is still the origin location the
// weight ladder ships from, but it no longer offers pickup.
//
export const BRACKET_WEIGHT_KG = 0.55;

// Heaviest order the "Brackets & Kits" profile still prices automatically.
export const MAX_ONLINE_SHIP_KG = 5000;

// The fence extensions (galvanised + aluminium) sit on their own "Fence
// Extensions" delivery profile since 2026-10-08, after a 25 x 900 mm order to
// Porongurup WA paid $100 postage on roughly $370 of AusPost. That profile is
// zoned VIC / NSW+ACT+SA+TAS / QLD / WA / NT and priced at cost off the AusPost
// Melbourne rate card (1 July 2026, regional zone of each state, cost at the
// middle of each band), capped at a $550 pallet per 500 kg up to 5,000 kg.
// Shopify rates each profile on its own weight and adds the two together, so
// bracket postage is exactly what it was. Mirrored in EXTENSION_MAX_SHIP_KG
// in data/fenceExtensions.js.
export const EXTENSION_MAX_SHIP_KG = 5000;

// One row per band in the Shopify profile, in the same order, so the two lists
// can be read side by side. 30–40kg and 40–60kg are both $100 by design.
// The last row stops at 150 brackets, where pricing goes by state.
export const SHIPPING_TIERS = [
  { maxKg: 1, price: 10.95 },
  { maxKg: 3, price: 15.95 },
  { maxKg: 6, price: 23.95 },
  { maxKg: 10, price: 29.95 },
  { maxKg: 13, price: 32.95 },
  { maxKg: 20, price: 49.95 },
  { maxKg: 23, price: 77.95 },
  { maxKg: 30, price: 84.95 },
  { maxKg: 40, price: 100 },
  { maxKg: 60, price: 100 },
  { maxKg: 82.5, price: 125 },
];

// Past 150 brackets the rate depends on the state, which the product page
// doesn't know, so it shows the cheapest (VIC) as a "from" price. VIC column of
// the Brackets & Kits profile.
export const BULK_TIERS_FROM = [
  { maxKg: 90, price: 237.2 },
  { maxKg: 110, price: 276 },
  { maxKg: 130, price: 331.2 },
  { maxKg: 160, price: 400.75 },
  { maxKg: 200, price: 496.8 },
  ...Array.from({ length: 10 }, (_, i) => ({ maxKg: (i + 1) * 500, price: (i + 1) * 550 })),
];

// Returns { type: "ship", price } up to 150 brackets, { type: "ship-from",
// price } past it, and { type: "pickup-only" } past 5,000kg, where the order
// needs a freight quote instead of an automatic rate.
export function bracketShippingEstimate(qty) {
  // Rounded to the gram: 200 x 0.55 is 110.00000000000001 in floating point,
  // which would tip it past the 110kg band edge Shopify itself treats as 110.
  const kg = Math.round(Math.max(1, Math.floor(Number(qty)) || 1) * BRACKET_WEIGHT_KG * 1000) / 1000;
  const tier = SHIPPING_TIERS.find((t) => kg <= t.maxKg);
  if (tier) return { type: "ship", price: tier.price };
  const bulk = BULK_TIERS_FROM.find((t) => kg <= t.maxKg);
  return bulk ? { type: "ship-from", price: bulk.price } : { type: "pickup-only" };
}

// ---------------------------------------------------------------------------
// Whole-cart weight
// ---------------------------------------------------------------------------
// The estimate above prices one product at a time, which is all a product page
// knows. Shopify rates the WHOLE cart though: every parcel product sits on the
// one "General profile", so a cart mixing brackets, extensions and kits is
// weighed as a single order against the bands above. A cart can therefore pass
// the limit while no single line looks heavy.
//
// Shopify stores each variant's weight in whatever unit it was saved in (the
// bracket is in GRAMS, the extensions in KILOGRAMS), so normalise before adding
// them up or a 550 g bracket reads as 550 kg.
const KG_PER_WEIGHT_UNIT = {
  KILOGRAMS: 1,
  GRAMS: 0.001,
  POUNDS: 0.45359237,
  OUNCES: 0.028349523125,
};

export function variantWeightKg(weight, weightUnit) {
  const factor = KG_PER_WEIGHT_UNIT[weightUnit];
  const value = Number(weight);
  if (!factor || !Number.isFinite(value)) return 0;
  return value * factor;
}

// Total shipping weight of a cart's lines, in kg, from the same weights Shopify
// rates the order on. Long Products (pickets, animal extension) carry a 0 kg
// weight on purpose because they sit on the freight-quote profile, so they add
// nothing here, which is correct.
export function cartWeightKg(lines) {
  return (lines || []).reduce((kg, line) => {
    const variant = line?.merchandise;
    const qty = Number(line?.quantity) || 0;
    return kg + variantWeightKg(variant?.weight, variant?.weightUnit) * qty;
  }, 0);
}

// True when either profile's share of the cart is past the heaviest order it
// still prices. With no rate to return Shopify rejects the checkout as "we
// don't ship to your address", so the customer reads a weight limit as an
// address fault. The cart has to catch it first and offer the pallet quote.
export function needsFreightQuote(generalKg, extensionKg = 0) {
  return generalKg > MAX_ONLINE_SHIP_KG || extensionKg > EXTENSION_MAX_SHIP_KG;
}
